import React, { useState, useEffect } from "react";
import { ChevronDown, Menu } from "lucide-react";
import { LOGO_MARK } from "../lib/api";
import { QuoteDialog } from "./QuoteDialog";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "../components/ui/sheet";

const portfolioMenu = [
  { id: "featured", label: "Featured" },
  { id: "portfolio", label: "Voice Work" },
  { id: "mastering", label: "Mastering" },
  { id: "music", label: "Music" },
];

const mobileLinks = [
  { id: "fxlab", label: "Demo" },
  { id: "services", label: "Services" },
  { id: "featured", label: "Featured" },
  { id: "portfolio", label: "Voice Work" },
  { id: "mastering", label: "Mastering" },
  { id: "music", label: "Music" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const goMobile = (id) => { setOpen(false); setTimeout(() => go(id), 250); };

  return (
    <nav data-testid="navbar" className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-[#0B0C10]/85 border-b border-white/10" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO_MARK} alt="E.O.VO" className="h-11 w-11 object-contain" />
          <div className="leading-none text-left">
            <div className="font-head font-bold tracking-[0.22em] text-lg">E.O.VO</div>
            <div className="text-[8px] tracking-[0.12em] text-[#A1A1AA] mt-1">VOICE · NARRATION · AUDIO</div>
          </div>
        </button>

        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => go("fxlab")} data-testid="nav-demo" className="text-sm text-[#A1A1AA] hover:text-[#C8A97E] transition-colors">Demo</button>
          <button onClick={() => go("services")} data-testid="nav-services" className="text-sm text-[#A1A1AA] hover:text-[#C8A97E] transition-colors">Services</button>

          <div className="relative group">
            <button data-testid="nav-portfolio" className="flex items-center gap-1 text-sm text-[#A1A1AA] hover:text-[#C8A97E] transition-colors">
              Portfolio <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="min-w-[190px] rounded-lg border border-white/10 bg-[#12141A] p-1.5 shadow-2xl">
                {portfolioMenu.map((m) => (
                  <button key={m.id} onClick={() => go(m.id)} data-testid={`nav-pf-${m.id}`}
                    className="w-full text-left px-3.5 py-2 rounded-md text-sm text-[#D4D4D8] hover:bg-[#C8A97E] hover:text-[#0B0C10] transition-colors">
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button onClick={() => go("contact")} data-testid="nav-contact" className="text-sm text-[#A1A1AA] hover:text-[#C8A97E] transition-colors">Contact</button>
          <QuoteDialog trigger={<Button data-testid="nav-quote" className="bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] rounded-sm font-semibold">Get a Quote</Button>} />
        </div>

        <div className="md:hidden flex items-center gap-2.5">
          <QuoteDialog trigger={<Button size="sm" data-testid="nav-quote-mobile" className="bg-[#C8A97E] text-[#0B0C10] rounded-sm font-semibold">Quote</Button>} />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-menu-toggle" aria-label="Open menu"
                className="h-10 w-10 grid place-items-center rounded-md border border-white/15 text-[#D4D4D8] hover:text-[#C8A97E] hover:border-[#C8A97E]/50 transition-colors">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" data-testid="mobile-menu" className="w-[78%] max-w-xs bg-[#0B0C10] border-l border-white/10 text-white p-0">
              <div className="flex items-center gap-3 px-6 h-[72px] border-b border-white/10">
                <img src={LOGO_MARK} alt="E.O.VO" className="h-9 w-9 object-contain" />
                <div className="font-head font-bold tracking-[0.22em]">E.O.VO</div>
              </div>
              <div className="flex flex-col p-3">
                {mobileLinks.map((m) => (
                  <button key={m.id} onClick={() => goMobile(m.id)} data-testid={`nav-mobile-${m.id}`}
                    className="w-full text-left px-4 py-3.5 rounded-md text-[15px] text-[#D4D4D8] hover:bg-white/5 hover:text-[#C8A97E] transition-colors border-b border-white/[0.04] last:border-0">
                    {m.label}
                  </button>
                ))}
                <div className="px-4 pt-5">
                  <QuoteDialog trigger={
                    <Button data-testid="nav-mobile-quote" className="w-full bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] rounded-sm font-semibold h-11">
                      Get a Quote
                    </Button>
                  } />
                </div>
                <SheetClose data-testid="nav-mobile-close" className="hidden" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
